import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  LogOut,
  ExternalLink,
  Plus,
  Pencil,
  Trash2,
  FileText,
  Megaphone,
  Eye,
  Newspaper,
  Loader2,
  MapPin,
  Hammer,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import CategoryBadge from "@/components/CategoryBadge";
import ArticleForm from "@/pages/admin/ArticleForm";
import CategoriesManager from "@/pages/admin/CategoriesManager";
import UsersManager from "@/pages/admin/UsersManager";
import SettingsManager from "@/pages/admin/SettingsManager";
import AdsManager from "@/pages/admin/AdsManager";
import SubscribersManager from "@/pages/admin/SubscribersManager";
import CommentsManager from "@/pages/admin/CommentsManager";
import ProfilePage from "@/pages/admin/ProfilePage";
import MediaManager from "@/pages/admin/MediaManager";
import ListingsManager from "@/pages/admin/ListingsManager";
import { formatDateTime, formatViews } from "@/lib/format";
import { SIGNAL_STATUSES } from "@/lib/categories";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const settings = useSettings();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [tab, setTab] = useState("articles");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const qc = useQueryClient();

  const { data: stats } = useQuery({
    queryKey: ["admin-stats"],
    queryFn: () => api.get("/admin/stats").then((r) => r.data),
  });
  const { data: articles, isLoading: aLoad } = useQuery({
    queryKey: ["admin-articles"],
    queryFn: () => api.get("/admin/articles").then((r) => r.data),
  });
  const { data: signals, isLoading: sLoad } = useQuery({
    queryKey: ["admin-signals"],
    queryFn: () => api.get("/admin/signals").then((r) => r.data),
  });

  const refreshAll = () => {
    qc.invalidateQueries();
  };

  const deleteArticle = async (id) => {
    if (!window.confirm("Изтриване на статията?")) return;
    try {
      await api.delete(`/admin/articles/${id}`);
      toast.success("Статията е изтрита.");
      refreshAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const updateSignal = async (id, status) => {
    try {
      await api.put(`/admin/signals/${id}`, { status });
      toast.success("Статусът е обновен.");
      refreshAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteSignal = async (id) => {
    if (!window.confirm("Изтриване на сигнала?")) return;
    try {
      await api.delete(`/admin/signals/${id}`);
      toast.success("Сигналът е изтрит.");
      refreshAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const statCards = [
    { label: "Статии", value: stats?.total_articles ?? "—", icon: Newspaper, color: "text-blue-600 bg-blue-50" },
    { label: "Публикувани", value: stats?.published ?? "—", icon: FileText, color: "text-green-600 bg-green-50" },
    { label: "Общо прегледи", value: stats ? formatViews(stats.total_views) : "—", icon: Eye, color: "text-amber-600 bg-amber-50" },
    { label: "Нови сигнали", value: stats?.new_signals ?? "—", icon: Megaphone, color: "text-purple-600 bg-purple-50" },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary font-display text-base font-extrabold text-white">
              П
            </span>
            <div>
              <div className="font-display text-sm font-extrabold leading-none text-slate-900">
                Редакторски панел
              </div>
              <div className="text-xs text-slate-500">{user?.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50"
              data-testid="admin-view-site"
            >
              <ExternalLink className="h-4 w-4" />
              <span className="hidden sm:inline">Виж сайта</span>
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Изход</span>
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {settings.maintenance_mode && (
          <div
            className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3"
            data-testid="admin-maintenance-banner"
          >
            <div className="flex items-center gap-2 text-sm font-semibold text-amber-800">
              <Hammer className="h-4 w-4 shrink-0" />
              Сайтът е в режим „в процес на обновяване“. Посетителите виждат страница за поддръжка; вие запазвате пълен достъп.
            </div>
            <div className="flex shrink-0 gap-2">
              <a
                href="/?preview=maintenance"
                target="_blank"
                rel="noreferrer"
                data-testid="maintenance-preview-link"
                className="rounded-lg bg-white px-3 py-1.5 text-xs font-bold text-amber-700 ring-1 ring-amber-300 hover:bg-amber-100"
              >
                Виж страницата
              </a>
              {isAdmin && (
                <button
                  onClick={() => setTab("settings")}
                  data-testid="maintenance-goto-settings"
                  className="rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-amber-600"
                >
                  Изключи в Настройки
                </button>
              )}
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="admin-stats">
          {statCards.map((s) => (
            <div key={s.label} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${s.color}`}>
                <s.icon className="h-5 w-5" />
              </div>
              <div className="mt-3 font-display text-2xl font-extrabold text-slate-900">
                {s.value}
              </div>
              <div className="text-xs font-medium text-slate-500">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="mt-8 flex gap-1 overflow-x-auto border-b border-slate-200">
          <TabButton active={tab === "articles"} onClick={() => setTab("articles")} testid="tab-articles">
            Статии
          </TabButton>
          <TabButton active={tab === "signals"} onClick={() => setTab("signals")} testid="tab-signals">
            Сигнали {stats?.new_signals ? `(${stats.new_signals})` : ""}
          </TabButton>
          <TabButton active={tab === "listings"} onClick={() => setTab("listings")} testid="tab-listings">
            Обяви {stats?.pending_listings ? `(${stats.pending_listings})` : ""}
          </TabButton>
          <TabButton active={tab === "comments"} onClick={() => setTab("comments")} testid="tab-comments">
            Коментари {stats?.pending_comments ? `(${stats.pending_comments})` : ""}
          </TabButton>
          <TabButton active={tab === "media"} onClick={() => setTab("media")} testid="tab-media">
            Медия
          </TabButton>
          <TabButton active={tab === "profile"} onClick={() => setTab("profile")} testid="tab-profile">
            Профил
          </TabButton>
          {isAdmin && (
            <>
              <TabButton active={tab === "categories"} onClick={() => setTab("categories")} testid="tab-categories">
                Рубрики
              </TabButton>
              <TabButton active={tab === "ads"} onClick={() => setTab("ads")} testid="tab-ads">
                Реклами
              </TabButton>
              <TabButton active={tab === "newsletter"} onClick={() => setTab("newsletter")} testid="tab-newsletter">
                Бюлетин {stats?.total_subscribers ? `(${stats.total_subscribers})` : ""}
              </TabButton>
              <TabButton active={tab === "users"} onClick={() => setTab("users")} testid="tab-users">
                Потребители
              </TabButton>
              <TabButton active={tab === "settings"} onClick={() => setTab("settings")} testid="tab-settings">
                Настройки
              </TabButton>
            </>
          )}
        </div>

        {tab === "comments" && <CommentsManager />}
        {tab === "listings" && <ListingsManager />}
        {tab === "media" && <MediaManager />}
        {tab === "profile" && <ProfilePage />}
        {tab === "categories" && isAdmin && <CategoriesManager />}
        {tab === "ads" && isAdmin && <AdsManager />}
        {tab === "newsletter" && isAdmin && <SubscribersManager />}
        {tab === "users" && isAdmin && <UsersManager />}
        {tab === "settings" && isAdmin && <SettingsManager />}

        {/* Articles tab */}
        {tab === "articles" && (
          <div className="mt-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-slate-900">
                Всички статии
              </h2>
              <button
                onClick={() => {
                  setEditing(null);
                  setFormOpen(true);
                }}
                data-testid="admin-new-article-btn"
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-bold text-white hover:bg-[#0041A8]"
              >
                <Plus className="h-4 w-4" />
                Нова статия
              </button>
            </div>

            {aLoad ? (
              <Loader />
            ) : (
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                <div className="divide-y divide-slate-100">
                  {(articles || []).map((a) => (
                    <div
                      key={a.id}
                      className="flex items-center gap-4 p-4 hover:bg-slate-50"
                      data-testid={`admin-article-row-${a.id}`}
                    >
                      <img
                        src={a.image_url}
                        alt=""
                        loading="lazy"
                        className="hidden h-14 w-24 shrink-0 rounded-lg object-cover sm:block"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <CategoryBadge category={a.category} asLink={false} />
                          {a.status === "draft" && (
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">
                              Чернова
                            </span>
                          )}
                          {a.is_featured && (
                            <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-600">
                              Водеща
                            </span>
                          )}
                        </div>
                        <h3 className="mt-1 truncate font-display text-sm font-bold text-slate-900">
                          {a.title}
                        </h3>
                        <div className="mt-0.5 flex items-center gap-3 text-xs text-slate-500">
                          <span>{a.author}</span>
                          <span className="inline-flex items-center gap-1">
                            <Eye className="h-3 w-3" />
                            {formatViews(a.views)}
                          </span>
                          <span>{formatDateTime(a.published_at)}</span>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <button
                          onClick={() => {
                            setEditing(a);
                            setFormOpen(true);
                          }}
                          data-testid={`admin-edit-${a.id}`}
                          className="rounded-lg p-2 text-slate-500 hover:bg-blue-50 hover:text-primary"
                          aria-label="Редактиране"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteArticle(a.id)}
                          data-testid={`admin-delete-${a.id}`}
                          className="rounded-lg p-2 text-slate-500 hover:bg-red-50 hover:text-red-600"
                          aria-label="Изтриване"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Signals tab */}
        {tab === "signals" && (
          <div className="mt-6">
            <h2 className="mb-4 font-display text-lg font-bold text-slate-900">
              Граждански сигнали
            </h2>
            {sLoad ? (
              <Loader />
            ) : (signals || []).length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-white p-12 text-center text-slate-500">
                Все още няма получени сигнали.
              </div>
            ) : (
              <div className="space-y-4">
                {signals.map((s) => (
                  <div
                    key={s.id}
                    className="rounded-xl border border-slate-200 bg-white p-5"
                    data-testid={`admin-signal-${s.id}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <CategoryBadge category={s.category} asLink={false} />
                          <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                            <MapPin className="h-3.5 w-3.5" />
                            {s.location}
                          </span>
                          <StatusBadge status={s.status} />
                        </div>
                        <h3 className="mt-2 font-display text-base font-bold text-slate-900">
                          {s.name}
                        </h3>
                        <div className="text-xs text-slate-500">
                          {[s.email, s.phone].filter(Boolean).join(" · ")}
                          {" · "}
                          {formatDateTime(s.created_at)}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <select
                          value={s.status}
                          onChange={(e) => updateSignal(s.id, e.target.value)}
                          data-testid={`admin-signal-status-${s.id}`}
                          className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm font-medium text-slate-700 outline-none focus:border-primary"
                        >
                          {SIGNAL_STATUSES.map((st) => (
                            <option key={st} value={st}>{st}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => deleteSignal(s.id)}
                          data-testid={`admin-signal-delete-${s.id}`}
                          className="rounded-lg p-2 text-slate-500 hover:bg-red-50 hover:text-red-600"
                          aria-label="Изтриване"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <p className="mt-3 rounded-lg bg-slate-50 p-3 text-sm leading-relaxed text-slate-700">
                      {s.description}
                    </p>
                    {s.image_url && (
                      <a
                        href={s.image_url}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        Приложена снимка
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <ArticleForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        article={editing}
        onSaved={refreshAll}
      />
    </div>
  );
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`relative px-4 py-2.5 text-sm font-semibold transition-colors ${
        active ? "text-primary" : "text-slate-500 hover:text-slate-800"
      }`}
    >
      {children}
      {active && <span className="absolute inset-x-0 -bottom-px h-0.5 bg-primary" />}
    </button>
  );
}

function StatusBadge({ status }) {
  const map = {
    Нов: "bg-blue-50 text-blue-700",
    "В преглед": "bg-amber-50 text-amber-700",
    Разрешен: "bg-green-50 text-green-700",
    Отхвърлен: "bg-slate-100 text-slate-500",
  };
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${map[status] || "bg-slate-100 text-slate-600"}`}>
      {status}
    </span>
  );
}

function Loader() {
  return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="h-7 w-7 animate-spin text-primary" />
    </div>
  );
}
